import { env } from '../../utils/env';

// Game code expects camelCase (healAmount, moveId, etc.). We map API snake_case to this shape.
export interface Item {
    id: string; // identifier, used as key
    name: string;
    description?: string;
    price?: number;
    healAmount?: number;
    healStatus?: string[];
    overworldOptions?: string[];
    battleOptions?: string[];
    catchRate?: number;
    restoreAmount?: number;
    moveId?: string;
    /** When held: consumed when HP drops below this % (e.g. 50 = Oran/Sitrus, 25 = pinch berries). */
    berryConsumeHpPercent?: number;
    implemented?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiItemResponse = any;

export default class ItemManager {
    store: Record<string, Item>;
    useAPI: boolean;

    constructor() {
        this.store = {};
        this.useAPI = true;
    }

    async loadItemsFromAPI(): Promise<void> {
        const API_BASE: string =
            (env as { VITE_API_BASE: string }).VITE_API_BASE || 'https://api.pkmng.orly.io/api';

        const response = await fetch(`${API_BASE}/items/game/`);
        if (!response.ok) {
            throw new Error(`Failed to fetch items: ${String(response.status)}`);
        }

        const itemsArray = (await response.json()) as ApiItemResponse[];
        if (!Array.isArray(itemsArray)) {
            return;
        }

        itemsArray.forEach((apiItem: ApiItemResponse) => {
            const identifier =
                typeof apiItem.identifier === 'string' ? apiItem.identifier : String(apiItem.id ?? '');
            const item: Item = {
                id: identifier,
                name: apiItem.name ?? '',
                ...(apiItem.description != null && { description: apiItem.description }),
                ...(apiItem.price != null && { price: Number(apiItem.price) }),
                ...(apiItem.heal_amount != null && { healAmount: Number(apiItem.heal_amount) }),
                ...(Array.isArray(apiItem.heal_status) && { healStatus: apiItem.heal_status }),
                ...(Array.isArray(apiItem.overworld_options) && {
                    overworldOptions: apiItem.overworld_options,
                }),
                ...(Array.isArray(apiItem.battle_options) && {
                    battleOptions: apiItem.battle_options,
                }),
                ...(apiItem.catch_rate != null && { catchRate: Number(apiItem.catch_rate) }),
                ...(apiItem.restore_amount != null && {
                    restoreAmount: Number(apiItem.restore_amount),
                }),
                ...(apiItem.move_id != null && { moveId: apiItem.move_id }),
                ...(apiItem.berry_consume_hp_percent != null && {
                    berryConsumeHpPercent: Number(apiItem.berry_consume_hp_percent),
                }),
                implemented: Boolean(apiItem.implemented),
            };
            this.store[identifier] = item;
        });
    }

    getItem(identifier: string): Item | undefined {
        return this.store[identifier];
    }

    addItem(item: Item): void {
        this.store[item.id] = item;
    }
}
